import { Permission, hasPermission, type GuidancePersona } from '@bookora/shared';

/**
 * Personas pick COPY, never visibility. Whether a step exists is decided per
 * step by permissions, because clinics build custom roles and a role key says
 * nothing about what a person may open. First match wins; the comments name
 * the system preset that lands on each rule (`SYSTEM_ROLE_PRESETS`).
 */
export type Persona = GuidancePersona;

/** Copy-only variants of `owner`. */
export type OwnerVariant = 'solo' | 'new-owner' | null;

export interface PersonaMembership {
  isDoctor: boolean;
  joinedAt: string;
}

const DAY_MS = 86_400_000;

export function resolvePersona(
  permissions: readonly string[],
  membership: PersonaMembership | null,
  _clinicType: 'CLINIC' | 'SOLO_DOCTOR' | null,
  _now: Date,
): Persona {
  const can = (p: Permission): boolean => hasPermission(permissions, p);
  // clinic_owner
  if (can(Permission.STAFF_MANAGE_ROLES) && can(Permission.SETTINGS_EDIT)) return 'owner';
  // doctor
  if (membership?.isDoctor) return 'doctor';
  // nurse: holds appointment.create AND session.create — clinical wins
  if (can(Permission.SESSION_CREATE)) return 'clinical';
  // receptionist
  if (can(Permission.APPOINTMENT_CREATE)) return 'front-desk';
  // accountant (holds patient.view, which must not exclude it)
  if (can(Permission.FINANCE_VIEW_PROFIT) || can(Permission.FINANCE_VIEW_COSTS)) return 'finance';
  return 'staff';
}

/** `solo` for a solo-doctor clinic, `new-owner` in the first day of membership. */
export function ownerVariant(
  persona: Persona,
  membership: PersonaMembership | null,
  clinicType: 'CLINIC' | 'SOLO_DOCTOR' | null,
  now: Date,
): OwnerVariant {
  if (persona !== 'owner') return null;
  // A fresh signup first: its copy is about returning to the setup, which is
  // where a brand-new solo doctor is headed too.
  const joined = membership ? Date.parse(membership.joinedAt) : Number.NaN;
  if (Number.isFinite(joined) && now.getTime() - joined < DAY_MS) return 'new-owner';
  if (clinicType === 'SOLO_DOCTOR') return 'solo';
  return null;
}
